import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  Mail, Lock, User, Phone, ArrowRight, CheckCircle, Globe,
  MapPin, Building, Search, ChevronRight, Users, Briefcase,
  Home, HeartHandshake, Zap, Shield, Network, Mail as MailIcon,
  ArrowLeft, Check, Eye, EyeOff, X
} from 'lucide-react';
import { Logo } from '../components/Logo';
import ExitIntentSurvey from '../components/ExitIntentSurvey';
import { logFunnel, trackDwellTime, getFunnelSessionId } from '../lib/funnel';

const GOOGLE_KEY = process.env.REACT_APP_GOOGLE_PLACES_KEY;

const BUSINESS_CATEGORIES = [
  { id: 'real_estate', label: 'Real Estate', icon: Home },
  { id: 'mortgage', label: 'Mortgage / Lending', icon: Building },
  { id: 'insurance', label: 'Insurance', icon: Shield },
  { id: 'legal', label: 'Legal / Attorney', icon: Briefcase },
  { id: 'financial', label: 'Financial / Wealth', icon: Zap },
  { id: 'accounting', label: 'Accounting / CPA', icon: Briefcase },
  { id: 'auto_sales', label: 'Auto Sales', icon: Building },
  { id: 'technology', label: 'Technology', icon: Network },
  { id: 'healthcare', label: 'Healthcare', icon: HeartHandshake },
  { id: 'marketing', label: 'Marketing / PR', icon: Users },
  { id: 'construction', label: 'Construction', icon: Building },
  { id: 'other', label: 'Other', icon: Globe },
];

const BUSINESS_SUBTYPES = {
  real_estate: [
    { id: 'brokerage', label: 'Real Estate Brokerage' },
    { id: 'property_mgmt', label: 'Property Management' },
    { id: 'development', label: 'Real Estate Development' },
    { id: 'title_company', label: 'Title Company' },
  ],
  mortgage: [
    { id: 'mortgage_brokerage', label: 'Mortgage Brokerage' },
    { id: 'mortgage_bank', label: 'Mortgage Bank' },
    { id: 'credit_union', label: 'Credit Union' },
  ],
  insurance: [
    { id: 'agency', label: 'Insurance Agency' },
    { id: 'broker', label: 'Insurance Broker' },
  ],
  legal: [
    { id: 'law_office', label: 'Law Office / Law Firm' },
    { id: 'solo_practice', label: 'Solo Practice' },
    { id: 'legal_services', label: 'Legal Services' },
  ],
  financial: [
    { id: 'wealth_mgmt', label: 'Wealth Management Firm' },
    { id: 'financial_planning', label: 'Financial Planning Firm' },
    { id: 'investment', label: 'Investment Firm' },
  ],
  accounting: [
    { id: 'cpa_firm', label: 'CPA Firm' },
    { id: 'accounting_firm', label: 'Accounting Firm' },
    { id: 'bookkeeping', label: 'Bookkeeping Service' },
    { id: 'tax_prep', label: 'Tax Preparation' },
  ],
  auto_sales: [
    { id: 'dealership', label: 'Dealership' },
    { id: 'independent_dealer', label: 'Independent Dealer' },
    { id: 'auto_broker', label: 'Auto Broker' },
  ],
};

const INSURANCE_LICENSE_TYPES = [
  { id: 'independent', label: 'Independent' },
  { id: 'captive', label: 'Captive' },
];

const CAPTIVE_CARRIERS = [
  'AAA',
  'Allstate',
  'American Family',
  'Erie Insurance',
  'Farm Bureau',
  'Farmers Insurance',
  'Liberty Mutual',
  'Nationwide',
  'State Farm',
  'Shelter Insurance',
  'USAA',
  'Other',
];

const CUSTOMER_OPTIONS = [
  'Homeowners', 'Small Business Owners', 'Corporations', 'Real Estate Investors',
  'First-Time Buyers', 'Renters', 'Seniors / Retirees', 'Young Professionals',
  'Families', 'High Net Worth Individuals',
];

const PARTNER_BUSINESSES = [
  'Real Estate Agents', 'Mortgage Brokers', 'Insurance Agents', 'Attorneys',
  'Financial Advisors', 'Home Inspectors', 'Title Companies', 'Appraisers',
  'CPAs / Accountants', 'Contractors', 'Property Managers', 'Interior Designers',
];

const LOCATION_RADII = [
  { value: '5mi', label: '5 miles', desc: 'Hyperlocal' },
  { value: '10mi', label: '10 miles', desc: 'Local' },
  { value: '25mi', label: '25 miles', desc: 'Metro area' },
  { value: '50mi', label: '50 miles', desc: 'Regional' },
  { value: 'statewide', label: 'Statewide', desc: 'Your state' },
  { value: 'nationwide', label: 'Nationwide', desc: 'All of USA' },
];

const TOTAL_STEPS = 10;

const StepDot = ({ step, current, label }) => {
  const done = current > step;
  const active = current === step;
  return (
    <div className="flex flex-col items-center gap-1">
      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
        done ? 'bg-green-500 text-white' : active ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'
      }`}>
        {done ? <Check className="w-3.5 h-3.5" /> : step}
      </div>
      {label && <span className={`text-[9px] hidden sm:block ${active ? 'text-primary font-medium' : 'text-muted-foreground'}`}>{label}</span>}
    </div>
  );
};

const ProgressBar = ({ step }) => {
  const labels = ['Account', 'Verify', 'Business', 'Category', 'Website', 'Customers', 'Partners', 'Location', 'Network', 'Preview'];
  return (
    <div className="w-full mb-6">
      <div className="flex items-end justify-between">
        {labels.map((l, i) => (
          <div key={i} className="flex-1 flex flex-col items-center relative">
            {i > 0 && <div className={`absolute top-3.5 -left-1/2 w-full h-0.5 transition-colors ${step > i + 1 ? 'bg-green-400' : step === i + 1 ? 'bg-primary/40' : 'bg-border'}`} />}
            <StepDot step={i + 1} current={step} label={l} />
          </div>
        ))}
      </div>
      <div className="mt-2 text-xs text-muted-foreground text-center">
        Step {step} of {TOTAL_STEPS}
      </div>
    </div>
  );
};

export const RegisterPage = () => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [verifyCode, setVerifyCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [fallbackCode, setFallbackCode] = useState(null);
  const [showFallbackCode, setShowFallbackCode] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [createdUser, setCreatedUser] = useState(null);
  const [createdSignature, setCreatedSignature] = useState(null);
  const [professions, setProfessions] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const businessInputRef = useRef(null);
  const autocompleteRef = useRef(null);
  // Tracks whether the visitor completed registration. Used by ExitIntentSurvey
  // to suppress the "why did you leave?" prompt when they actually signed up.
  const registrationCompletedRef = useRef(false);

  const [formData, setFormData] = useState({
    full_name: '', email: '', phone: '', password: '',
    company: '', street_address: '', city: '', state: '', zip_code: '',
    profession_id: '', custom_profession: '', website: '',
    business_category: '', target_customers: [], target_businesses: [],
    location_radius: '', network_consent: false,
    linkedin_url: '', twitter_url: '', facebook_url: '', instagram_url: '',
    insurance_carrier: '', insurance_specialty: [],
    insurance_license_type: '', insurance_carrier_other: '',
    business_subtype: '',
    account_type: 'individual',
    business_name: '', business_industry: '', business_size: '', business_description: '',
  });

  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const referralCode = searchParams.get('ref') || '';
  const inviteToken = searchParams.get('invite') || '';
  const inviteBusinessId = searchParams.get('business') || '';

  // Funnel tracking refs — each event must only fire once per page lifecycle
  const startedRef = useRef(false);
  const professionLoggedRef = useRef(false);
  const passwordLoggedRef = useRef(false);
  const submittedLoggedRef = useRef(false);

  const update = (field, value) => {
    if (!startedRef.current) {
      startedRef.current = true;
      logFunnel('register_started', { field });
    }
    if (field === 'profession_id' && value && !professionLoggedRef.current) {
      professionLoggedRef.current = true;
      logFunnel('profession_selected', { profession_id: String(value).slice(0, 40) });
    }
    if (field === 'password' && typeof value === 'string' && value.length >= 8 && !passwordLoggedRef.current) {
      passwordLoggedRef.current = true;
      logFunnel('password_entered');
    }
    setFormData(p => ({ ...p, [field]: value }));
  };
  const toggleList = (field, value) => {
    if (!startedRef.current) {
      startedRef.current = true;
      logFunnel('register_started', { field });
    }
    setFormData(p => ({
      ...p, [field]: p[field].includes(value) ? p[field].filter(x => x !== value) : [...p[field], value]
    }));
  };

  // Funnel: page opened + dwell-time
  useEffect(() => {
    logFunnel('register_opened', { ref: referralCode || null });
    const stopDwell = trackDwellTime('register_dwell');
    return () => { stopDwell(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load professions
  useEffect(() => {
    api.getProfessions().then(d => setProfessions(d.professions || [])).catch(() => {});
  }, []);

  // Load Google Places script
  useEffect(() => {
    if (!GOOGLE_KEY) return;
    if (window.google?.maps?.places) return;
    const existing = document.querySelector('script[data-gplaces]');
    if (existing) return;
    const s = document.createElement('script');
    s.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_KEY}&libraries=places`;
    s.async = true;
    s.setAttribute('data-gplaces', '1');
    document.head.appendChild(s);
  }, []);

  // Init autocomplete on step 3
  useEffect(() => {
    if (step !== 3) return;
    // Auto-fill company from business_name for business accounts
    if (formData.account_type === 'business' && formData.business_name && !formData.company) {
      update('company', formData.business_name);
    }
    if (!businessInputRef.current) return;
    const init = () => {
      if (!window.google?.maps?.places) return;
      if (autocompleteRef.current) return;
      const ac = new window.google.maps.places.Autocomplete(businessInputRef.current, {
        types: ['establishment'],
        componentRestrictions: { country: 'us' },
        fields: ['name', 'formatted_address', 'address_components', 'website', 'geometry'],
      });
      ac.addListener('place_changed', () => {
        const place = ac.getPlace();
        if (!place.name) return;
        const comps = place.address_components || [];
        const get = (type, short = false) => comps.find(c => c.types.includes(type))?.[short ? 'short_name' : 'long_name'] || '';
        const streetNum = get('street_number');
        const route = get('route');
        setFormData(p => ({
          ...p,
          company: place.name || p.company,
          street_address: [streetNum, route].filter(Boolean).join(' ') || p.street_address,
          city: get('locality') || get('sublocality') || p.city,
          state: get('administrative_area_level_1', true) || p.state,
          zip_code: get('postal_code') || p.zip_code,
          website: place.website || p.website,
        }));
      });
      autocompleteRef.current = ac;
    };
    if (window.google?.maps?.places) { init(); }
    else {
      const t = setInterval(() => { if (window.google?.maps?.places) { init(); clearInterval(t); } }, 300);
      return () => clearInterval(t);
    }
  }, [step]);

  // Step validators
  const canProceed = useCallback(() => {
    if (step === 1) {
      const base = formData.full_name && formData.email && formData.phone && formData.password && formData.password.length >= 6;
      if (formData.account_type === 'business') return base && !!formData.business_name;
      return base;
    }
    if (step === 2) return verifyCode.length >= 4;
    if (step === 3) return formData.company && formData.city && formData.state;
    if (step === 4) {
      if (!formData.business_category) return false;
      // Require sub-type if available for the category
      const subtypes = BUSINESS_SUBTYPES[formData.business_category];
      if (subtypes && subtypes.length > 0 && !formData.business_subtype) return false;
      if (formData.business_category === 'insurance') {
        if (!formData.insurance_license_type) return false;
        if (formData.insurance_license_type === 'captive' && !formData.insurance_carrier) return false;
        if (formData.insurance_carrier === 'Other' && !formData.insurance_carrier_other) return false;
        return formData.insurance_specialty.length > 0;
      }
      return true;
    }
    if (step === 5) return true;
    if (step === 6) return formData.target_customers.length > 0;
    if (step === 7) return true;
    if (step === 8) return !!formData.location_radius;
    if (step === 9) return true;
    return true;
  }, [step, formData, verifyCode]);

  const sendMockCode = async () => {
    if (!formData.email) return toast.error('Enter your email first');
    if (sendingCode || cooldown > 0) return;
    setSendingCode(true);
    try {
      const response = await api.sendEmailOTP(formData.email);
      setCodeSent(true);
      if (response.status === 'fallback' && response.fallback_code) {
        setFallbackCode(response.fallback_code);
        setVerifyCode(response.fallback_code);
        setShowFallbackCode(true);
        toast.success('Your verification code is displayed below.');
      } else {
        if (response.backup_code) {
          setFallbackCode(response.backup_code);
          setVerifyCode(response.backup_code);
          setShowFallbackCode(true);
        }
        toast.success(`Verification code sent to ${formData.email}.`);
      }
      setCooldown(30);
      const timer = setInterval(() => {
        setCooldown(prev => {
          if (prev <= 1) { clearInterval(timer); return 0; }
          return prev - 1;
        });
      }, 1000);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to send code';
      toast.error(detail);
    } finally {
      setSendingCode(false);
    }
  };

  const verifyPhoneCode = async (code) => {
    if (!code || code.length < 4) return false;
    try {
      const result = await api.verifyEmailOTP(formData.email, code);
      return result.valid;
    } catch {
      return false;
    }
  };

  const [emailError, setEmailError] = useState('');

  const handleNext = async () => {
    if (!canProceed()) {
      const msgs = {
        1: 'Fill in all required fields (min 6 char password)',
        2: 'Enter the verification code',
        3: 'Enter your business name, city and state',
        4: 'Select your business category',
        6: 'Select at least one customer type',
        8: 'Select your service radius',
      };
      return toast.error(msgs[step] || 'Please complete this step');
    }
    // Step 1: check if email already registered
    if (step === 1) {
      setLoading(true);
      setEmailError('');
      try {
        const res = await api.checkEmail(formData.email);
        if (res.exists) {
          setEmailError('This email is already registered. Please sign in instead.');
          setLoading(false);
          return;
        }
      } catch {
        // If check fails, let them proceed — registration will catch duplicates
      }
      setLoading(false);
    }
    // Step 2: verify the OTP
    if (step === 2) {
      setLoading(true);
      const valid = await verifyPhoneCode(verifyCode);
      setLoading(false);
      if (!valid) return toast.error('Invalid verification code. Please try again.');
    }
    if (step < 9) return setStep(s => s + 1);
    // Step 9 → Register
    await handleRegister();
  };

  const handleRegister = async () => {
    setLoading(true);
    try {
      const profId = formData.profession_id || 'real_estate_agent';
      const funnelSid = getFunnelSessionId();
      // Funnel: submit clicked (after pre-checks pass)
      if (!submittedLoggedRef.current) {
        submittedLoggedRef.current = true;
        logFunnel('register_submitted');
      }
      const payload = { ...formData, referral_code: referralCode, signup_source: referralCode || null, funnel_session_id: funnelSid, profession_id: profId, invite_token: inviteToken };
      const res = await register(payload);
      setCreatedUser(res?.user || payload);
      // Auto-create first signature
      try {
        const sigPayload = {
          name: `${formData.full_name}'s Signature`,
          template_style: 'modern',
          full_name: formData.full_name,
          title: formData.custom_profession || (professions.find(p => p.id === profId)?.name) || 'Professional',
          email: formData.email,
          phone: formData.phone,
          company: formData.company,
          website: formData.website,
          city: formData.city,
          state: formData.state,
          street_address: formData.street_address,
        };
        const sigRes = await api.createSignature(sigPayload);
        setCreatedSignature(sigRes);
        // Funnel: signature generated (first one)
        logFunnel('signature_generated', { template: 'modern' });
      } catch {}
      registrationCompletedRef.current = true;
      setStep(10);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const profName = professions.find(p => p.id === formData.profession_id)?.name || formData.custom_profession || 'Professional';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4" data-testid="register-page">
      <div className="w-full max-w-xl">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-2 mb-6" data-testid="register-logo-link">
          <img src="/brand/logo-icon.png" alt="ConnectMail" className="w-9 h-9 rounded-lg bg-primary p-1 shadow-lg" />
          <span className="font-semibold text-2xl tracking-tight font-['Outfit'] text-white">ConnectMail</span>
        </Link>

        <div className="bg-background rounded-2xl shadow-2xl p-6 sm:p-8">
          {step < 10 && <ProgressBar step={step} />}

          {/* ── STEP 1: Account Info ── */}
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <h2 className="text-xl font-bold font-['Outfit']">Create your account</h2>
                <p className="text-muted-foreground text-sm mt-1">Start your free 30-day trial — no credit card required</p>
              </div>
              {/* Account Type Selector */}
              <div className="grid grid-cols-2 gap-3" data-testid="account-type-selector">
                <button
                  onClick={() => update('account_type', 'individual')}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${formData.account_type === 'individual' ? 'border-primary bg-primary/5 ring-2 ring-primary/20' : 'border-border hover:border-primary/40'}`}
                  data-testid="account-type-individual"
                >
                  <User className="w-5 h-5 mb-1.5 text-primary" />
                  <p className="text-sm font-semibold">Individual</p>
                  <p className="text-[10px] text-muted-foreground">Personal professional account</p>
                </button>
                <button
                  onClick={() => update('account_type', 'business')}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${formData.account_type === 'business' ? 'border-primary bg-primary/5 ring-2 ring-primary/20' : 'border-border hover:border-primary/40'}`}
                  data-testid="account-type-business"
                >
                  <Building className="w-5 h-5 mb-1.5 text-primary" />
                  <p className="text-sm font-semibold">Business</p>
                  <p className="text-[10px] text-muted-foreground">Company or organization</p>
                </button>
              </div>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input placeholder={formData.account_type === 'business' ? "Contact Person Name *" : "Full Name *"} value={formData.full_name} onChange={e => update('full_name', e.target.value)} className="pl-10" data-testid="fullname-input" />
              </div>
              {formData.account_type === 'business' && (
                <div className="relative">
                  <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input placeholder="Business Name *" value={formData.business_name} onChange={e => update('business_name', e.target.value)} className="pl-10" data-testid="business-name-input" />
                </div>
              )}
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input type="email" placeholder="Email address *" value={formData.email} onChange={e => { update('email', e.target.value); setEmailError(''); }} className={`pl-10 ${emailError ? 'border-red-500 focus-visible:ring-red-500' : ''}`} data-testid="email-input" />
              </div>
              {emailError && (
                <p className="text-red-500 text-xs -mt-2 flex items-center gap-1" data-testid="email-error">
                  <X className="w-3 h-3" />{emailError} <Link to="/login" className="underline font-medium ml-1">Sign in</Link>
                </p>
              )}
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input type="tel" placeholder="Phone number *" value={formData.phone} onChange={e => update('phone', e.target.value)} className="pl-10" data-testid="phone-input" />
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input type={showPassword ? "text" : "password"} placeholder="Password (min 6 chars) *" value={formData.password} onChange={e => update('password', e.target.value)} className="pl-10 pr-10" data-testid="password-input" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors" data-testid="toggle-password-visibility" tabIndex={-1}>
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <Button className="w-full" onClick={handleNext} disabled={!canProceed()} data-testid="next-step-btn">
                Continue <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
              <p className="text-center text-sm text-muted-foreground">Already have an account? <Link to="/login" className="text-primary hover:underline font-medium">Sign in</Link></p>
            </div>
          )}

          {/* ── STEP 2: Phone Verification ── */}
          {step === 2 && (
            <div className="space-y-5">
              <div>
                <h2 className="text-xl font-bold font-['Outfit']">Verify your email</h2>
                <p className="text-muted-foreground text-sm mt-1">
                  {codeSent ? `Enter the code sent to ${formData.email}` : `We'll send a verification code to ${formData.email}`}
                </p>
              </div>
              <div className="flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mx-auto">
                <MailIcon className="w-7 h-7 text-primary" />
              </div>
              {!codeSent ? (
                <Button className="w-full" onClick={sendMockCode} disabled={sendingCode} data-testid="send-code-btn">
                  {sendingCode ? 'Sending...' : <>Send Verification Code <ArrowRight className="w-4 h-4 ml-1" /></>}
                </Button>
              ) : (
                <>
                  <div>
                    <label className="text-sm font-medium block mb-1.5">Verification code</label>
                    <Input
                      type="text" inputMode="numeric" maxLength={6}
                      placeholder="Enter 6-digit code"
                      value={verifyCode}
                      onChange={e => setVerifyCode(e.target.value.replace(/\D/g, ''))}
                      className="text-center text-xl tracking-widest font-mono"
                      data-testid="verify-code-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Check your email inbox (and spam folder). Use the code from the <strong>most recent</strong> email.</p>
                  </div>
                  <Button className="w-full" onClick={handleNext} disabled={!canProceed()} data-testid="verify-btn">
                    Verify & Continue <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                  <button
                    className={`text-xs w-full text-center ${cooldown > 0 ? 'text-muted-foreground cursor-not-allowed' : 'text-primary hover:underline'}`}
                    onClick={() => { if (cooldown <= 0) { setCodeSent(false); setVerifyCode(''); setFallbackCode(null); setShowFallbackCode(false); } }}
                    disabled={cooldown > 0}
                  >
                    {cooldown > 0 ? `Resend code in ${cooldown}s` : 'Resend code'}
                  </button>
                  {fallbackCode && !showFallbackCode && (
                    <button
                      className="text-xs w-full text-center text-amber-600 hover:underline font-medium mt-1"
                      onClick={() => { setShowFallbackCode(true); setVerifyCode(fallbackCode); }}
                      data-testid="didnt-receive-btn"
                    >
                      Didn't receive the code? Click here
                    </button>
                  )}
                  {showFallbackCode && fallbackCode && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center mt-2" data-testid="fallback-code">
                      <p className="text-xs text-amber-800 mb-1">Use this code to continue:</p>
                      <p className="text-2xl font-bold tracking-widest text-amber-900 font-mono">{fallbackCode}</p>
                    </div>
                  )}
                </>
              )}
              <button className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" onClick={() => setStep(1)}>
                <ArrowLeft className="w-3.5 h-3.5" />Back
              </button>
            </div>
          )}

          {/* ── STEP 3: Business Search ── */}
          {step === 3 && (
            <div className="space-y-4">
              <div>
                <h2 className="text-xl font-bold font-['Outfit']">
                  {formData.account_type === 'business' ? 'Business location' : 'Find your business'}
                </h2>
                <p className="text-muted-foreground text-sm mt-1">
                  {formData.account_type === 'business' ? 'Enter your business address details' : 'Search for your business to auto-fill your details'}
                </p>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground z-10" />
                <input
                  ref={businessInputRef}
                  placeholder="Search business name..."
                  defaultValue={formData.company}
                  className="w-full pl-10 pr-4 py-2 border border-input bg-background rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  data-testid="business-search-input"
                />
              </div>
              <p className="text-xs text-muted-foreground -mt-2">Type to search — your address will auto-fill from Google</p>
              {/* Manual fallback */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Business Name *</label>
                  <Input placeholder="e.g. Smith Realty" value={formData.company} onChange={e => update('company', e.target.value)} data-testid="company-input" />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Street Address</label>
                  <Input placeholder="123 Main St" value={formData.street_address} onChange={e => update('street_address', e.target.value)} />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">City *</label>
                  <Input placeholder="Dallas" value={formData.city} onChange={e => update('city', e.target.value)} data-testid="city-input" />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">State *</label>
                  <Input placeholder="TX" value={formData.state} onChange={e => update('state', e.target.value)} data-testid="state-input" />
                </div>
              </div>
              <div className="flex gap-2 pt-1">
                <Button variant="outline" onClick={() => setStep(2)} className="flex-1"><ArrowLeft className="w-4 h-4 mr-1" />Back</Button>
                <Button className="flex-1" onClick={handleNext} disabled={!canProceed()} data-testid="next-step-btn">
                  Continue <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          )}

          {/* ── STEP 4: Business Category ── */}
          {step === 4 && (
            <div className="space-y-4">
              <div>
                <h2 className="text-xl font-bold font-['Outfit']">What type of business?</h2>
                <p className="text-muted-foreground text-sm mt-1">Choose the category that best describes your business</p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {BUSINESS_CATEGORIES.map(cat => {
                  const Icon = cat.icon;
                  const selected = formData.business_category === cat.id;
                  return (
                    <button
                      key={cat.id}
                      onClick={() => {
                        update('business_category', cat.id);
                        update('business_subtype', '');
                        update('insurance_license_type', '');
                        update('insurance_carrier', '');
                        // Also set profession if not set
                        if (!formData.profession_id) {
                          const profMap = { real_estate: 'real_estate_agent', mortgage: 'mortgage_broker', insurance: 'insurance_agent', legal: 'attorney', financial: 'financial_advisor', accounting: 'accountant', auto_sales: 'custom' };
                          if (profMap[cat.id]) update('profession_id', profMap[cat.id]);
                        }
                      }}
                      className={`p-3 rounded-xl border-2 text-left transition-all ${selected ? 'border-primary bg-primary/5 text-primary' : 'border-border hover:border-primary/40 hover:bg-muted/50'}`}
                      data-testid={`cat-${cat.id}`}
                    >
                      <Icon className={`w-5 h-5 mb-1.5 ${selected ? 'text-primary' : 'text-muted-foreground'}`} />
                      <span className="text-xs font-medium block">{cat.label}</span>
                    </button>
                  );
                })}
              </div>

              {/* Business Sub-type */}
              {formData.business_category && BUSINESS_SUBTYPES[formData.business_category] && (
                <div className="space-y-2 p-4 bg-muted/30 rounded-xl border border-border">
                  <p className="text-sm font-semibold">Business Type</p>
                  <div className="flex flex-wrap gap-2">
                    {BUSINESS_SUBTYPES[formData.business_category].map(sub => {
                      const sel = formData.business_subtype === sub.id;
                      return (
                        <button key={sub.id} onClick={() => update('business_subtype', sub.id)}
                          className={`px-3 py-2 rounded-lg text-xs font-medium border-2 transition-all ${sel ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:border-primary/40'}`}
                          data-testid={`subtype-${sub.id}`}
                        >
                          {sel && <Check className="w-3 h-3 inline mr-1" />}{sub.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Insurance-specific: License Type + Carrier + Specialty */}
              {formData.business_category === 'insurance' && (
                <div className="space-y-3 p-4 bg-muted/30 rounded-xl border border-border">
                  <p className="text-sm font-semibold">Insurance Details</p>

                  {/* License Type: Independent or Captive */}
                  <div>
                    <label className="text-xs text-muted-foreground mb-1.5 block">License Type *</label>
                    <div className="flex gap-2">
                      {INSURANCE_LICENSE_TYPES.map(lt => {
                        const sel = formData.insurance_license_type === lt.id;
                        return (
                          <button key={lt.id} onClick={() => { update('insurance_license_type', lt.id); if (lt.id === 'independent') update('insurance_carrier', ''); }}
                            className={`flex-1 px-3 py-2.5 rounded-lg text-sm font-medium border-2 transition-all ${sel ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:border-primary/40'}`}
                            data-testid={`license-${lt.id}`}
                          >
                            {sel && <Check className="w-3 h-3 inline mr-1" />}{lt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Captive Carrier Dropdown */}
                  {formData.insurance_license_type === 'captive' && (
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Carrier *</label>
                      <select
                        value={formData.insurance_carrier}
                        onChange={e => update('insurance_carrier', e.target.value)}
                        className="w-full h-10 px-3 border border-input rounded-md text-sm bg-background"
                        data-testid="captive-carrier-select"
                      >
                        <option value="">Select your carrier...</option>
                        {CAPTIVE_CARRIERS.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                      {formData.insurance_carrier === 'Other' && (
                        <Input
                          placeholder="Enter carrier name..."
                          value={formData.insurance_carrier_other}
                          onChange={e => update('insurance_carrier_other', e.target.value)}
                          className="mt-2"
                          data-testid="carrier-other-input"
                        />
                      )}
                    </div>
                  )}

                  {/* Specialty */}
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Specialty * <span className="text-[10px] text-muted-foreground">(select all that apply)</span></label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {[
                        { id: 'pc', label: 'Property & Casualty' },
                        { id: 'life', label: 'Life Insurance' },
                        { id: 'health', label: 'Health Insurance' },
                        { id: 'auto', label: 'Auto Insurance' },
                        { id: 'commercial', label: 'Commercial' },
                        { id: 'home', label: 'Homeowners' },
                      ].map(sp => {
                        const sel = formData.insurance_specialty.includes(sp.id);
                        return (
                          <button key={sp.id} onClick={() => toggleList('insurance_specialty', sp.id)}
                            className={`px-3 py-2 rounded-lg text-xs font-medium border-2 transition-all ${sel ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:border-primary/40'}`}
                            data-testid={`specialty-${sp.id}`}
                          >
                            {sel && <Check className="w-3 h-3 inline mr-1" />}{sp.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-2 pt-1">
                <Button variant="outline" onClick={() => setStep(3)} className="flex-1"><ArrowLeft className="w-4 h-4 mr-1" />Back</Button>
                <Button className="flex-1" onClick={handleNext} disabled={!canProceed()} data-testid="next-step-btn">
                  Continue <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          )}

          {/* ── STEP 5: Website URL ── */}
          {step === 5 && (
            <div className="space-y-4">
              <div>
                <h2 className="text-xl font-bold font-['Outfit']">Your website</h2>
                <p className="text-muted-foreground text-sm mt-1">
                  {formData.website ? 'We found this from your business listing — confirm or edit it' : 'Enter your website URL or skip for now'}
                </p>
              </div>
              {formData.website && (
                <div className="flex items-center gap-2 bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg px-3 py-2">
                  <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                  <span className="text-xs text-green-700 dark:text-green-400">Auto-detected from Google Places</span>
                </div>
              )}
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input placeholder="https://yourbusiness.com" value={formData.website} onChange={e => update('website', e.target.value)} className="pl-10" data-testid="website-input" />
              </div>
              <div className="flex gap-2 pt-1">
                <Button variant="outline" onClick={() => setStep(4)} className="flex-1"><ArrowLeft className="w-4 h-4 mr-1" />Back</Button>
                <Button className="flex-1" onClick={handleNext} data-testid="next-step-btn">
                  {formData.website ? 'Confirm & Continue' : 'Skip for now'} <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          )}

          {/* ── STEP 6: Describe Customers ── */}
          {step === 6 && (
            <div className="space-y-4">
              <div>
                <h2 className="text-xl font-bold font-['Outfit']">Describe your customers</h2>
                <p className="text-muted-foreground text-sm mt-1">Select all that apply — this helps us match you with the right professionals</p>
              </div>
              <div className="flex flex-wrap gap-2">
                {CUSTOMER_OPTIONS.map(opt => {
                  const sel = formData.target_customers.includes(opt);
                  return (
                    <button key={opt} onClick={() => toggleList('target_customers', opt)}
                      className={`px-3 py-1.5 rounded-full text-sm border-2 transition-all ${sel ? 'border-primary bg-primary text-primary-foreground' : 'border-border hover:border-primary/50'}`}
                      data-testid={`customer-${opt.replace(/\s+/g, '-').toLowerCase()}`}
                    >
                      {sel && <Check className="w-3 h-3 inline mr-1" />}{opt}
                    </button>
                  );
                })}
              </div>
              <div className="flex gap-2 pt-1">
                <Button variant="outline" onClick={() => setStep(5)} className="flex-1"><ArrowLeft className="w-4 h-4 mr-1" />Back</Button>
                <Button className="flex-1" onClick={handleNext} disabled={!canProceed()} data-testid="next-step-btn">
                  Continue <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          )}

          {/* ── STEP 7: Partner Businesses ── */}
          {step === 7 && (
            <div className="space-y-4">
              <div>
                <h2 className="text-xl font-bold font-['Outfit']">Which businesses do you work with?</h2>
                <p className="text-muted-foreground text-sm mt-1">Select the types of professionals you partner or refer business with</p>
              </div>
              <div className="flex flex-wrap gap-2">
                {PARTNER_BUSINESSES.map(biz => {
                  const sel = formData.target_businesses.includes(biz);
                  return (
                    <button key={biz} onClick={() => toggleList('target_businesses', biz)}
                      className={`px-3 py-1.5 rounded-full text-sm border-2 transition-all ${sel ? 'border-primary bg-primary text-primary-foreground' : 'border-border hover:border-primary/50'}`}
                      data-testid={`partner-${biz.replace(/\s+\/?\s*/g, '-').toLowerCase()}`}
                    >
                      {sel && <Check className="w-3 h-3 inline mr-1" />}{biz}
                    </button>
                  );
                })}
              </div>
              <div className="flex gap-2 pt-1">
                <Button variant="outline" onClick={() => setStep(6)} className="flex-1"><ArrowLeft className="w-4 h-4 mr-1" />Back</Button>
                <Button className="flex-1" onClick={handleNext} data-testid="next-step-btn">
                  {formData.target_businesses.length ? 'Continue' : 'Skip for now'} <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          )}

          {/* ── STEP 8: Location Radius ── */}
          {step === 8 && (
            <div className="space-y-4">
              <div>
                <h2 className="text-xl font-bold font-['Outfit']">How far do you serve?</h2>
                <p className="text-muted-foreground text-sm mt-1">This helps surface your profile to the right local professionals</p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {LOCATION_RADII.map(r => {
                  const sel = formData.location_radius === r.value;
                  return (
                    <button key={r.value} onClick={() => update('location_radius', r.value)}
                      className={`p-3 rounded-xl border-2 text-center transition-all ${sel ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'}`}
                      data-testid={`radius-${r.value}`}
                    >
                      <MapPin className={`w-5 h-5 mx-auto mb-1 ${sel ? 'text-primary' : 'text-muted-foreground'}`} />
                      <span className={`text-sm font-medium block ${sel ? 'text-primary' : ''}`}>{r.label}</span>
                      <span className="text-xs text-muted-foreground">{r.desc}</span>
                    </button>
                  );
                })}
              </div>
              <div className="flex gap-2 pt-1">
                <Button variant="outline" onClick={() => setStep(7)} className="flex-1"><ArrowLeft className="w-4 h-4 mr-1" />Back</Button>
                <Button className="flex-1" onClick={handleNext} disabled={!canProceed()} data-testid="next-step-btn">
                  Continue <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          )}

          {/* ── STEP 9: Network Coach ── */}
          {step === 9 && (
            <div className="space-y-5">
              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Network className="w-8 h-8 text-primary" />
                </div>
                <h2 className="text-xl font-bold font-['Outfit']">Your Network Coach</h2>
                <p className="text-muted-foreground text-sm mt-1 max-w-sm mx-auto">
                  Hi {formData.full_name.split(' ')[0]}! You're almost set up. Let ConnectMail find professionals already in your email contacts who are on the platform.
                </p>
              </div>
              <div className="bg-muted/50 rounded-xl p-4 space-y-2">
                <div className="flex items-start gap-3">
                  <MailIcon className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold">Connect your email contacts</p>
                    <p className="text-xs text-muted-foreground mt-0.5">We'll match your existing contacts with ConnectMail members and send professional introductions on your behalf — turning cold contacts into warm referral partners.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Users className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold">Start earning referral commissions faster</p>
                    <p className="text-xs text-muted-foreground mt-0.5">The average ConnectMail user connects with 8 new referral partners in their first week using this feature.</p>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Button className="w-full" onClick={() => { update('network_consent', true); handleRegister(); }} disabled={loading} data-testid="network-connect-btn">
                  {loading ? <span className="flex items-center gap-2"><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Creating your profile...</span> : <><MailIcon className="w-4 h-4 mr-1" />Connect my contacts & continue</>}
                </Button>
                <Button variant="outline" className="w-full" onClick={() => { update('network_consent', false); handleRegister(); }} disabled={loading} data-testid="remind-later-btn">
                  Remind me later
                </Button>
              </div>
              <button className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mx-auto" onClick={() => setStep(8)}>
                <ArrowLeft className="w-3.5 h-3.5" />Back
              </button>
            </div>
          )}

          {/* ── STEP 10: Profile Preview ── */}
          {step === 10 && (
            <div className="space-y-5">
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <CheckCircle className="w-9 h-9 text-green-600" />
                </div>
                <h2 className="text-xl font-bold font-['Outfit']">Your profile is ready!</h2>
                <p className="text-muted-foreground text-sm mt-1">We've created your professional profile and first email signature</p>
              </div>

              {/* Profile Card Preview */}
              <div className="border border-border rounded-xl p-5 space-y-3 bg-muted/20">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center text-white font-bold text-lg">
                    {formData.full_name?.[0]}
                  </div>
                  <div>
                    <p className="font-semibold">{formData.full_name}</p>
                    <p className="text-sm text-primary">{profName}</p>
                    {formData.company && <p className="text-xs text-muted-foreground">{formData.company}</p>}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                  {formData.city && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{formData.city}, {formData.state}</span>}
                  {formData.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{formData.phone}</span>}
                  {formData.email && <span className="flex items-center gap-1 col-span-2"><Mail className="w-3 h-3" />{formData.email}</span>}
                  {formData.website && <span className="flex items-center gap-1 col-span-2"><Globe className="w-3 h-3" />{formData.website}</span>}
                </div>
              </div>

              {/* Signature Created */}
              <div className="flex items-center gap-2 bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg px-3 py-2.5">
                <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                <span className="text-sm text-green-700 dark:text-green-400">
                  {createdSignature ? `Email signature "${createdSignature.name || formData.full_name + "'s Signature"}" created` : 'First email signature ready'}
                </span>
              </div>

              {/* What's next bullets */}
              <div className="space-y-2 text-sm">
                {[
                  'Browse the Marketplace to place ads in partner signatures',
                  'Publish your own signature to start earning $10 per filled ad slot',
                  'Use Smart Connect to reach out to professionals in the Directory',
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <ChevronRight className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                    <span className="text-muted-foreground">{item}</span>
                  </div>
                ))}
              </div>

              <Button className="w-full" onClick={() => navigate('/dashboard')} data-testid="go-to-dashboard-btn">
                Go to Dashboard <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          )}
        </div>
      </div>
      <ExitIntentSurvey triggerOnUnmount submittedRef={registrationCompletedRef} />
    </div>
  );
};

export default RegisterPage;
